import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Radio, 
  Input, 
  Button, 
  message, 
  Typography, 
  Progress, 
  Modal, 
  List, 
  Avatar, 
  Alert,
  Spin,
  Row,
  Col 
} from 'antd';
import { useParams, Navigate } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { doc, getDoc, updateDoc, arrayUnion, setDoc, writeBatch } from 'firebase/firestore';
import { useAuth } from '../components/auth/AuthContext';
import { db } from '../firebase/config';

const { TextArea } = Input;
const { Title, Text } = Typography;

const POLL_QUESTIONS = [
  "Do you support this candidate's economic policies?",
  "Are you satisfied with their past performance?",
  "Would you recommend this candidate to others?"
];

const styles = {
    container: {
      padding: '24px',
      maxWidth: '1200px',
      margin: '100px auto',
    },
    backButton: {
        marginTop: '24px',
        display: 'flex',
        justifyContent: 'center'
      },
    profileSection: {
      marginBottom: '24px',
    },
    profileCard: {
      textAlign: 'center',
      padding: '24px',
      background: '#fff',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    pollsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px',
      marginBottom: '24px',
    },
    pollCard: {
      background: '#fff',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    commentsSection: {
      background: '#fff',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    statsContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: '20px',
      marginTop: '16px',
    },
    statItem: {
      padding: '12px 24px',
      background: '#f5f5f5',
      borderRadius: '8px',
      minWidth: '120px',
    },
    avatar: {
      marginBottom: '16px',
    },
    buttonGroup: {
      marginTop: '16px',
      display: 'flex',
      gap: '12px',
      justifyContent: 'center',
    }
  };
  


const ProfilePage = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [voteResults, setVoteResults] = useState({});
  const [comment, setComment] = useState('');
  const [userVotes, setUserVotes] = useState({});
  const [showComments, setShowComments] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [votedCandidate, setVotedCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [questionsAnswered, setQuestionsAnswered] = useState(0);

  useEffect(() => {
    if (currentUser) {
      const initializeData = async () => {
        try {
          setLoading(true);
          await Promise.all([
            fetchProfile(),
            checkUserVotingStatus()
          ]);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      initializeData();
    }
  }, [id, currentUser]);

  // Add this new function to calculate total unique voters
const calculateTotalVoters = (voteResults) => {
    const questionData = voteResults?.question1 || {};
    const yesVotes = questionData.yes || 0;
    const noVotes = questionData.no || 0;
    return yesVotes + noVotes;
  };
  

  const checkUserVotingStatus = async () => {
    try {
      const userVotesDoc = await getDoc(doc(db, 'userVotes', currentUser.uid));
      if (userVotesDoc.exists()) {
        const votesData = userVotesDoc.data();
        setHasVoted(!!votesData.hasVoted);
        setVotedCandidate(votesData.votedFor);
        setUserVotes(votesData);
        
        if (votesData.hasVoted && votesData.votedFor && votesData.votedFor !== id) {
          message.warning('You have already voted for another candidate', 5);
        }
  
        const answeredCount = Object.keys(votesData.questions || {}).length;
        setQuestionsAnswered(answeredCount);
      } else {
        await setDoc(doc(db, 'userVotes', currentUser.uid), {
          hasVoted: false,
          votedFor: null,
          votedAt: null,
          questions: {}
        });
      }
    } catch (error) {
      console.error('Error checking voting status:', error);
      throw new Error('Failed to check voting status');
    }
  };
  
  const fetchProfile = async () => {
    try {
      const profileDoc = await getDoc(doc(db, 'users', id));
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        setProfile({ id: profileDoc.id, ...profileData });
        setVoteResults(profileData.votes || {});
      } else {
        throw new Error('Profile not found');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw new Error('Failed to load profile');
    }
  };

  const handleVote = async (questionIndex, vote) => {
    if (!currentUser) {
      message.error('Please login to vote');
      return;
    }

    try {
      if (hasVoted) {
        message.error(`You have already voted for a candidate${votedCandidate ? ` (ID: ${votedCandidate})` : ''}`);
        return;
      }

      const batch = writeBatch(db);
      const updatedVotes = {
        ...voteResults,
        [`question${questionIndex + 1}`]: {
          ...voteResults[`question${questionIndex + 1}`],
          [vote]: (voteResults[`question${questionIndex + 1}`]?.[vote] || 0) + 1
        }
      };

      const candidateRef = doc(db, 'users', id);
      batch.update(candidateRef, {
        votes: updatedVotes
      });

      const userVotesRef = doc(db, 'userVotes', currentUser.uid);
      const voteData = {
        questions: {
          ...userVotes.questions,
          [`question${questionIndex + 1}`]: vote
        }
      };

      const newQuestionsAnswered = questionsAnswered + 1;
      if (newQuestionsAnswered === POLL_QUESTIONS.length) {
        voteData.hasVoted = true;
        voteData.votedFor = id;
        voteData.votedAt = new Date().toISOString();
      }

      batch.set(userVotesRef, voteData, { merge: true });
      await batch.commit();

      setVoteResults(updatedVotes);
      setQuestionsAnswered(newQuestionsAnswered);
      
      if (newQuestionsAnswered === POLL_QUESTIONS.length) {
        setHasVoted(true);
        setVotedCandidate(id);
        message.success('All votes recorded successfully!');
      } else {
        message.success(`Question ${questionIndex + 1} vote recorded. ${POLL_QUESTIONS.length - newQuestionsAnswered} questions remaining.`);
      }

      setUserVotes(prev => ({
        ...prev,
        ...voteData
      }));

    } catch (error) {
      console.error('Error recording vote:', error);
      message.error('Failed to record vote');
    }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;

    try {
      const newComment = {
        text: comment.trim(),
        userId: currentUser.uid,
        userEmail: currentUser.email,
        createdAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'users', id), {
        comments: arrayUnion(newComment)
      });

      setComment('');
      message.success('Comment added successfully!');
      await fetchProfile();
    } catch (error) {
      console.error('Error adding comment:', error);
      message.error('Failed to add comment');
    }
  };

  const calculateVotePercentage = (questionIndex, type) => {
    const questionData = voteResults[`question${questionIndex + 1}`] || {};
    const yesVotes = questionData.yes || 0;
    const noVotes = questionData.no || 0;
    const total = yesVotes + noVotes;

    if (total === 0) return 0;
    return type === 'yes' ? (yesVotes / total) * 100 : (noVotes / total) * 100;
  };

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
    {/* Profile Section */}
    <div style={styles.profileSection}>
      <Card style={styles.profileCard}>
        <Avatar size={120} src={profile?.profileImage} style={styles.avatar} />
        <Title level={2}>{profile?.username}</Title>
        <Text style={{ fontSize: '16px', color: '#666' }}>
          {profile?.district}, {profile?.state}
        </Text>
        <div style={styles.statsContainer}>
          <div style={styles.statItem}>
            <Title level={4}>Total Voters</Title>
            <Text strong>{calculateTotalVoters(voteResults)}</Text>
          </div>
          <div style={styles.statItem}>
            <Title level={4}>Comments</Title>
            <Text strong>{profile?.comments?.length || 0}</Text>
          </div>
        </div>
      </Card>
    </div>

    {/* Alerts Section */}
    {hasVoted && votedCandidate && votedCandidate !== id && (
      <Alert
        message="You have already voted"
        description={`You have already cast your vote for another candidate (ID: ${votedCandidate})`}
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />
    )}

    {!hasVoted && (
      <Alert
        message={`Voting Progress: ${questionsAnswered}/${POLL_QUESTIONS.length} questions answered`}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
    )}

    {/* Polls Grid */}
    <div style={styles.pollsContainer}>
      {POLL_QUESTIONS.map((question, index) => (
        <Card key={index} style={styles.pollCard}>
          <Title level={4}>{question}</Title>
          <Radio.Group
            onChange={(e) => handleVote(index, e.target.value)}
            value={userVotes.questions?.[`question${index + 1}`]}
            disabled={hasVoted || userVotes.questions?.[`question${index + 1}`]}
            style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '12px' }}
          >
            <Radio.Button value="yes">Yes</Radio.Button>
            <Radio.Button value="no">No</Radio.Button>
          </Radio.Group>
          {(hasVoted || userVotes.questions?.[`question${index + 1}`]) && (
            <Progress
              style={{ marginTop: '16px' }}
              percent={calculateVotePercentage(index, 'yes')}
              format={percent => `${Math.round(percent)}% Yes`}
            />
          )}
        </Card>
      ))}
    </div>

    {/* Comments Section */}
    <div style={styles.commentsSection}>
      <Title level={3}>Comments</Title>
      <TextArea
        rows={4}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Add your comment..."
        maxLength={500}
      />
      <div style={styles.buttonGroup}>
        <Button 
          type="primary" 
          onClick={handleComment} 
          disabled={!comment.trim()}
        >
          Add Comment
        </Button>
        <Button onClick={() => setShowComments(true)}>
          View Comments ({profile?.comments?.length || 0})
        </Button>
      </div>
    </div>

    {/* Comments Modal */}
    <Modal
      title="Comments"
      visible={showComments}
      onCancel={() => setShowComments(false)}
      footer={null}
      width={600}
    >
      <List
        dataSource={profile?.comments || []}
        renderItem={item => (
          <List.Item>
            <List.Item.Meta
              avatar={<Avatar>{item.userEmail[0].toUpperCase()}</Avatar>}
              title={item.userEmail}
              description={item.text}
            />
            <Text type="secondary">
              {new Date(item.createdAt).toLocaleDateString()}
            </Text>
          </List.Item>
        )}
        locale={{ emptyText: 'No comments yet' }}
      />
    </Modal>
    <div style={styles.backButton}>
        <Button 
          type="primary" 
          icon={<LeftOutlined />} 
          onClick={() => navigate(-1)}
          size="large"
        >
          Go Back
        </Button>
      </div>
  </div>  );};

export default ProfilePage;

